import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAxios } from '../../core/_axios';
import usePageTitle from '../../hooks/usePageTitle';
import ManageAgencyForm from './ManageAgencyForm';
import ManageAgencyAdmin from './ManageAgencyAdmin';
import ManageAgencyUsers from './ManageAgencyUsers';
import { CONTAINER_CONTENT_CLASS } from '../signatures/Signatures';
import useAgency from '../../hooks/useAgency';
import { useAuth } from '../../AuthContext';

export function ManageSpecificAgencyRoutes({
    isForCurrentAgency = false,
}: {
    isForCurrentAgency?: boolean;
}) {

    const axios = useAxios();
    const { currentUser } = useAuth();

    const {
        pathname
    } = useLocation();

    const params = useParams();

    usePageTitle('Manage Agency');

    const {
        loading,
        loadingError,
        agency,
        setAgency,
        onLoadAgency: loadAgency,
    } = useAgency({
        axios,
        isForCurrentAgency,
        agencyUniqueId: isForCurrentAgency ? null : params.agencyUniqueId || null
    });

    if (loading) {
        return (
            <div className="container-fixed">
                <LoadingSpinner />
            </div>
        );
    }

    if (loadingError) {
        return (
            <div className="container-fixed">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingError}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={loadAgency}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return <>
        <div className="container-fixed">
            <div className={CONTAINER_CONTENT_CLASS}>
                <div className="flex flex-col justify-center gap-2">
                    <h1 className="text-xl font-semibold leading-none text-gray-900">
                        {`Manage ${agency.name}`}
                    </h1>
                </div>
                {false && <div className="flex items-center gap-2.5">
                    <a className="btn btn-sm btn-light" href="#">
                        Go to Teams
                    </a>
                </div>}
            </div>
        </div>
        <div className="container-fixed">
            <div className='mb-5'>
                <ul className="flex flex-wrap text-sm font-medium text-center text-gray-500 border-b border-gray-200 dark:border-gray-700 dark:text-gray-400">
                    <li className="me-2">
                        <Link
                            to={
                                isForCurrentAgency
                                    ? `/manage-agency/details`
                                    : `/admin/agencies/${agency.unique_identifier}/details`
                            }
                            className={[
                                (isForCurrentAgency
                                    ? pathname === `/manage-agency/details`
                                    : pathname === `/admin/agencies/${agency.unique_identifier}/details`)
                                    ? 'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500'
                                    : 'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}
                        >
                            Details
                        </Link>
                    </li>
                    <li className="me-2">
                        <Link
                            to={
                                isForCurrentAgency
                                    ? `/manage-agency/users`
                                    : `/admin/agencies/${agency.unique_identifier}/users`
                            }
                            className={[
                                (isForCurrentAgency
                                    ? pathname === `/manage-agency/users`
                                    : pathname === `/admin/agencies/${agency.unique_identifier}/users`)
                                    ? 'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500'
                                    : 'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}
                        >
                            Users
                        </Link>
                    </li>
                    {currentUser?.is_super_super_user && <li className="me-2">
                        <Link
                            to={
                                isForCurrentAgency
                                    ? `/manage-agency/admin`
                                    : `/admin/agencies/${agency.unique_identifier}/admin`
                            }
                            className={[
                                (isForCurrentAgency
                                    ? pathname === `/manage-agency/admin`
                                    : pathname === `/admin/agencies/${agency.unique_identifier}/admin`)
                                    ? 'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500'
                                    : 'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}
                        >
                            Admin
                        </Link>
                    </li>}
                    {false && <>
                        <li className="me-2">
                            <Link
                                to={
                                    isForCurrentAgency
                                        ? `/manage-agency/users`
                                        : `/admin/agencies/${agency.unique_identifier}/users`
                                }
                                className={[
                                    (isForCurrentAgency
                                        ? pathname === `/manage-agency/users`
                                        : pathname === `/admin/agencies/${agency.unique_identifier}/users`)
                                        ? 'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500'
                                        : 'text-gray-500 dark:text-gray-400',
                                    'inline-block p-4'
                                ].join(' ')}
                            >
                                Users
                            </Link>
                        </li>
                        <li className="me-2">
                            <Link
                                to={
                                    isForCurrentAgency
                                        ? `/manage-agency/add-user`
                                        : `/admin/agencies/${agency.unique_identifier}/add-user`
                                }
                                className={[
                                    (isForCurrentAgency
                                        ? pathname === `/manage-agency/add-user`
                                        : pathname === `/admin/agencies/${agency.unique_identifier}/add-user`)
                                        ? 'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500'
                                        : 'text-gray-500 dark:text-gray-400',
                                    'inline-block p-4'
                                ].join(' ')}
                            >
                                Add User
                            </Link>
                        </li>
                        <li className="me-2">
                            <Link
                                to={
                                    isForCurrentAgency
                                        ? `/manage-agency/billing`
                                        : `/admin/agencies/${agency.unique_identifier}/billing`
                                }
                                className={[
                                    (isForCurrentAgency
                                        ? pathname.startsWith(`/manage-agency/billing`)
                                        : pathname.startsWith(`/admin/agencies/${agency.unique_identifier}/billing`))
                                        ? 'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500'
                                        : 'text-gray-500 dark:text-gray-400',
                                    'inline-block p-4'
                                ].join(' ')}
                            >
                                Billing
                            </Link>
                        </li>
                    </>}
                </ul>
            </div>
            <Routes>
                <Route path="/details" element={
                    <ManageAgencyForm
                        agency={agency}
                        setAgency={setAgency}
                        isForCurrentAgency={isForCurrentAgency}
                    />}
                />
                <Route path="/users" element={
                    <ManageAgencyUsers
                        agency={agency}
                    />}
                />
                {currentUser?.is_super_super_user && <Route path="/admin" element={
                    <ManageAgencyAdmin
                        agency={agency}
                        setAgency={setAgency}
                    />}
                />}
                {/*}
                <Route path="/old-users" element={
                    <UsersList
                        agency={agency}
                        isForCurrentAgency={isForCurrentAgency}
                    />}
                />
                <Route path="/add-user" element={
                    <ManageAgencyForm
                        agency={agency}
                        isForCurrentAgency={isForCurrentAgency}
                    />}
                />
                <Route path="/billing/*" element={
                    <AgencyBillingForm
                        agencyUniqueIdentifier={isForCurrentAgency ? null : agency.unique_identifier}
                        isForCurrentAgency={isForCurrentAgency}
                    />}
                />
                */}
                {/* Redirect to details page if no match */}
                {isForCurrentAgency ? <Route
                    path="*"
                    element={<Navigate
                        to={`/manage-agency/details`}
                        replace
                    />}
                /> : <Route
                    path="*"
                    element={<Navigate
                        to={`/admin/agencies/${agency.unique_identifier}/details`}
                        replace
                    />}
                />}
            </Routes>
        </div>
    </>
}